import React, { useState, useEffect } from "react";
import { FaEllipsisH, FaPlus } from "react-icons/fa";
import apiService from "../../services/apiService";
import apiV2Service from "../../services/apiV2Service";
import spaceManagementService from "../../services/host-apiServices/spaceManagementService";
import { getUser } from "../../services/localService";
import { showApiMessage } from "../../utils/messageHandler";
import moment from "moment";
import AnnouncementFormModal from "../../components/AnnouncementFormModal";
import Pagination from "../../components/Pagination";
import { Command, CommandInput, CommandList, CommandItem } from "../../shadcn/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../shadcn/ui/popover";
import { Calendar } from "../../shadcn/ui/calendar";
import { Badge } from "../../shadcn/ui/badge";
import { ChevronDownIcon, Globe } from "lucide-react";
import EditAnnouncementModal from "../../components/EditAnnouncementModal";
import logger from "../../utils/logger";
import { FeatureAccess } from "../../utils/featureAccessControl";
import ResponsiveTable from "../../components/ResponsiveTable";
import IconWordResponsiveButton from "../../components/IconWordResponsiveButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../shadcn/ui/dialog";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Textarea } from "../../shadcn/ui/textarea";
import { Checkbox } from "../../shadcn/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/ui/select";

const Announcements = () => {
  const currentUser = getUser();
  const canCreate = FeatureAccess.canCreateAnnouncement(currentUser);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: "",
    selectedRoles: [],
    userIds: [],
    datetimeToSend: new Date(moment().add(1, "hour").toDate()),
    actionType: "newShipmentRequest",
    ctaValue: "",
    data: {},
    hasCTA: false,
    imageUrls: [],
    priority: "LOW",
    token: "",
    topic: "all_users",
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [announcementToConfirm, setAnnouncementToConfirm] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [selectedDate, setSelectedDate] = useState(undefined); // New state for date picker
  const [selectedTime, setSelectedTime] = useState("10:00:00"); // New state for time picker

  useEffect(() => {
    fetchAnnouncements();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Combine date and time when either changes
  useEffect(() => {
    if (selectedDate && selectedTime) {
      const [hours, minutes, seconds] = selectedTime.split(":");
      const combined = new Date(selectedDate);
      combined.setHours(hours, minutes, seconds);
      setNewAnnouncement(prev => ({ ...prev, datetimeToSend: combined }));
    }
  }, [selectedDate, selectedTime]);

  const fetchAnnouncements = () => {
    logger.log("Fetching announcements...");
    setIsLoading(true);
    apiService
      .getCreatedNotifications(currentPage, pageSize)
      .then(response => {
        logger.log("Raw response:", response);

        // Handle different response structures
        let announcements = [];
        let totalCount = 0;

        if (response.data) {
          // Handle nested notifications if present
          if (
            response.data.privateNotifications ||
            response.data.publicNotifications ||
            response.data.roleBasedNotifications
          ) {
            const privateNotifs = response.data.privateNotifications || [];
            const publicNotifs = response.data.publicNotifications || [];
            const roleBasedNotifs = response.data.roleBasedNotifications || [];
            announcements = [...privateNotifs, ...publicNotifs, ...roleBasedNotifs];
            totalCount = announcements.length;
          }
          // Check if response.data is an array directly
          else if (Array.isArray(response.data)) {
            announcements = response.data;
            totalCount = response.data.length;
          }
          // Check if response.data has a content property (paged response)
          else if (response.data.content && Array.isArray(response.data.content)) {
            announcements = response.data.content;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
          // Check if response.data has a data property
          else if (response.data.data && Array.isArray(response.data.data)) {
            announcements = response.data.data;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
        }

        if (announcements.length >= 0) {
          logger.log("Parsed announcements:", announcements);
          setAnnouncements(announcements);

          // Try to get total count from headers as fallback
          if (totalCount === announcements.length && response.headers && response.headers["x-total-count"]) {
            totalCount = parseInt(response.headers["x-total-count"]);
          }

          setTotalPages(Math.ceil(totalCount / pageSize));
        } else {
          logger.error("Invalid response format:", response.data);
          showApiMessage(
            {
              response: {
                status: 400,
                data: { message: "Invalid data received from server" },
              },
            },
            "fetch announcements"
          );
        }
        setIsLoading(false);
      })
      .catch(error => {
        logger.error("Error fetching announcements:", error);
        showApiMessage(error, "fetch announcements");
        setIsLoading(false);
      });
  };

  const fetchRoles = () => {
    logger.log("Fetching organization roles...");

    // Get current user's organization type ID for filtering
    const userOrgTypeId =
      currentUser?.user?.organization?.type?.id ||
      currentUser?.organization?.type?.id ||
      currentUser?.organization?.typeId ||
      currentUser?.user?.organizationId;
    const userRoleCode =
      currentUser?.user?.organizationRole?.code || currentUser?.organizationRole?.code || currentUser?.role;

    apiV2Service
      .getOrganizationRoles({ active: true, limit: 100 })
      .then(response => {
        if (response.data && Array.isArray(response.data)) {
          let roles = response.data;

          // Filter roles based on user's organization type
          if (userRoleCode !== "ROLE_ADMINISTRATOR") {
            // Main org type (sub-accounts) roles are always allowed
            const allowedTypeIds = [userOrgTypeId];

            // Host Property Manager (Type 3) can also send to Host Corporate (6) and Host Individual (7)
            if (userOrgTypeId === 3) {
              allowedTypeIds.push(6, 7);
            }

            // Property Distribution Host (Type 2) can send to Corporate (4) and Individual (5)
            if (userOrgTypeId === 2) {
              allowedTypeIds.push(4, 5);
            }

            roles = roles.filter(role => allowedTypeIds.includes(role.organizationTypeId));
          }

          // Map the organization roles to what the component expects
          const formattedRoles = roles.map(role => ({
            value: role.code, // Use role code as value
            label: role.name, // Use role name as label
            description: role.description, // Keep description if available
            organizationTypeId: role.organizationTypeId,
          }));
          setAvailableRoles(formattedRoles);
          logger.log("Formatted and filtered organization roles:", formattedRoles);
        }
      })
      .catch(error => {
        logger.error("Error fetching organization roles:", error);
        showApiMessage(error, "fetch organization roles");
      });
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setNewAnnouncement(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteRole = roleToDelete => {
    setNewAnnouncement(prev => ({
      ...prev,
      selectedRoles: prev.selectedRoles.filter(role => role !== roleToDelete),
    }));
  };

  const handleOpenCreateModal = () => {
    setNewAnnouncement({
      title: "",
      message: "",
      selectedRoles: [],
      userIds: [],
      datetimeToSend: new Date(moment().add(1, "hour").toDate()),
      actionType: "newShipmentRequest",
      ctaValue: "",
      data: {},
      hasCTA: false,
      imageUrls: [],
      priority: "LOW",
      token: "",
      topic: "all_users",
    });
    setFormErrors({});
    setSelectedDate(undefined);
    setSelectedTime("10:00:00");
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const localTime = moment(newAnnouncement.datetimeToSend);

    setAnnouncementToConfirm({
      ...newAnnouncement,
      title: newAnnouncement.title,
      message: newAnnouncement.message,
      datetimeToSend: localTime.format("YYYY-MM-DDTHH:mm:ss"),
      actionType: newAnnouncement.actionType,
      ctaValue: newAnnouncement.ctaValue,
      data: newAnnouncement.data,
      hasCTA: newAnnouncement.hasCTA,
      imageUrls: newAnnouncement.imageUrls,
      priority: newAnnouncement.priority,
      token: newAnnouncement.token,
      topic: newAnnouncement.topic,
      userIds: newAnnouncement.userIds || [],
    });
    setShowCreateModal(false);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = () => {
    logger.log("Submitting new announcement...");
    // Keep the local time as is for display
    const displayTime = moment(announcementToConfirm.datetimeToSend);
    logger.log("Selected local time:", displayTime.format("YYYY-MM-DD HH:mm:ss"));

    // Convert to UTC for the server
    const utcTime = moment(announcementToConfirm.datetimeToSend).utc();
    logger.log("UTC time for server:", utcTime.format("YYYY-MM-DD HH:mm:ss"));

    const pushNotificationPayload = {
      title: announcementToConfirm.title,
      message: announcementToConfirm.message,
      datetimeToSend: utcTime.format("YYYY-MM-DDTHH:mm:ss"),
      ctaValue: announcementToConfirm.ctaValue || "",
      hasCTA: announcementToConfirm.hasCTA || false,
      imageUrls: announcementToConfirm.imageUrls || [],
      priority: announcementToConfirm.priority || "LOW",
      token: announcementToConfirm.token || "",
    };

    if (announcementToConfirm.selectedRoles.length > 0) {
      pushNotificationPayload.roles = announcementToConfirm.selectedRoles;
    }

    if (announcementToConfirm.userIds && announcementToConfirm.userIds.length > 0) {
      pushNotificationPayload.userIds = announcementToConfirm.userIds;
    }

    return apiService
      .createAnnouncement(pushNotificationPayload)
      .then(response => {
        if (response.status === 200 || response.status === 201) {
          showApiMessage(response, "send announcement");
          setNewAnnouncement({
            title: "",
            message: "",
            selectedRoles: [],
            userIds: [],
            datetimeToSend: new Date(moment().add(1, "hour").toDate()),
            actionType: "newShipmentRequest",
            ctaValue: "",
            data: {},
            hasCTA: false,
            imageUrls: [],
            priority: "LOW",
            token: "",
            topic: "all_users",
          });
          setShowConfirmModal(false);
          setShowCreateModal(false);
          fetchAnnouncements();
        }
        return response;
      })
      .catch(error => {
        logger.error("Error sending announcement:", error);
        showApiMessage(error, "send announcement");
        throw error;
      });
  };

  const handleEdit = announcement => {
    setEditingAnnouncement(announcement);
    setShowEditModal(true);
  };

  const handleUpdateAnnouncement = formData => {
    return apiService
      .updateAnnouncement(formData.id, {
        id: formData.id,
        title: formData.title,
        message: formData.message,
        datetimeToSend: formData.datetimeToSend,
      })
      .then(response => {
        if (response.status === 200) {
          showApiMessage(response, "update announcement");
          fetchAnnouncements();
        }
        return response;
      })
      .catch(error => {
        logger.error("Error updating announcement:", error);
        showApiMessage(error, "update announcement");
        throw error;
      });
  };

  const handlePageChange = data => {
    setCurrentPage(data.selected - 1);

    // Fetch new data after page change
    setIsLoading(true);
    apiService
      .getCreatedNotifications(data.selected, pageSize)
      .then(response => {
        logger.log("Raw response:", response);

        // Handle different response structures
        let announcements = [];
        let totalCount = 0;

        if (response.data) {
          // Handle nested notifications if present
          if (
            response.data.privateNotifications ||
            response.data.publicNotifications ||
            response.data.roleBasedNotifications
          ) {
            const privateNotifs = response.data.privateNotifications || [];
            const publicNotifs = response.data.publicNotifications || [];
            const roleBasedNotifs = response.data.roleBasedNotifications || [];
            announcements = [...privateNotifs, ...publicNotifs, ...roleBasedNotifs];
            totalCount = announcements.length;
          }
          // Check if response.data is an array directly
          else if (Array.isArray(response.data)) {
            announcements = response.data;
            totalCount = response.data.length;
          }
          // Check if response.data has a content property (paged response)
          else if (response.data.content && Array.isArray(response.data.content)) {
            announcements = response.data.content;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
          // Check if response.data has a data property
          else if (response.data.data && Array.isArray(response.data.data)) {
            announcements = response.data.data;
            totalCount = response.data.totalElements || response.data.total || announcements.length;
          }
        }

        if (announcements.length >= 0) {
          logger.log("Parsed announcements:", announcements);
          setAnnouncements(announcements);

          // Try to get total count from headers as fallback
          if (totalCount === announcements.length && response.headers && response.headers["x-total-count"]) {
            totalCount = parseInt(response.headers["x-total-count"]);
          }

          setTotalPages(Math.ceil(totalCount / pageSize));
        } else {
          logger.error("Invalid response format:", response.data);
          showApiMessage(
            {
              response: {
                status: 400,
                data: { message: "Invalid data received from server" },
              },
            },
            "fetch announcements"
          );
        }
        setIsLoading(false);
      })
      .catch(error => {
        logger.error("Error fetching announcements:", error);
        showApiMessage(error, "fetch announcements");
        setIsLoading(false);
      });
  };

  const formatCreatedAt = createdAt => {
    if (!createdAt) return "N/A";
    const date = moment({
      year: createdAt.year,
      month: createdAt.monthValue - 1,
      date: createdAt.dayOfMonth,
      hour: createdAt.hour,
      minute: createdAt.minute,
      second: createdAt.second,
    });
    return date.format("MMMM D, YYYY h:mm A");
  };

  const formatDateTime = dateTime => {
    if (!dateTime) return "N/A";
    const date = moment.utc({
      year: dateTime.year,
      month: dateTime.monthValue - 1,
      date: dateTime.dayOfMonth,
      hour: dateTime.hour,
      minute: dateTime.minute,
      second: dateTime.second,
    });
    return date.local().format("MMMM D, YYYY h:mm A");
  };

  const isTimeInFuture = dateTime => {
    if (!dateTime) return false;

    // Create a moment object from the datetime components
    const publishDate = moment.utc({
      year: dateTime.year,
      month: dateTime.monthValue - 1, // Moment months are 0-based
      date: dateTime.dayOfMonth,
      hour: dateTime.hour,
      minute: dateTime.minute,
      second: dateTime.second,
    });

    // Get current time in UTC
    const now = moment.utc();

    // Debug logs to help verify the comparison
    logger.log("Publish date:", publishDate.format());
    logger.log("Current time:", now.format());
    logger.log("Is future?", publishDate.isAfter(now));

    return publishDate.isAfter(now);
  };

  // When displaying selected roles in chips
  const getDisplayLabel = roleValue => {
    const role = availableRoles.find(r => r.value === roleValue);
    return role ? role.label : roleValue;
  };

  const handleFileUpload = async files => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Convert FileList to Array
      const fileArray = Array.from(files);

      // Upload files using spaceManagementService
      const response = await spaceManagementService.uploadMedia(fileArray);

      if (response.status === 200 || response.status === 201) {
        const uploadedUrls = response.data;

        // Add uploaded URLs to existing imageUrls
        setNewAnnouncement(prev => ({
          ...prev,
          imageUrls: [...prev.imageUrls, ...uploadedUrls],
        }));

        setUploadProgress(100);
        showApiMessage(response, "upload images");
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      logger.error("Error uploading files:", error);
      showApiMessage(error, "upload images");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveImage = indexToRemove => {
    setNewAnnouncement(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, index) => index !== indexToRemove),
    }));
  };

  const validateForm = () => {
    const errors = {};

    // Required fields validation
    if (!newAnnouncement.title.trim()) {
      errors.title = "Title is required";
    }

    if (!newAnnouncement.message.trim()) {
      errors.message = "Message is required";
    }

    // Validate JSON data if provided
    if (newAnnouncement.data && typeof newAnnouncement.data === "string") {
      try {
        JSON.parse(newAnnouncement.data);
      } catch (error) {
        errors.data = "Invalid JSON format";
      }
    }

    // Validate datetime
    if (!newAnnouncement.datetimeToSend || new Date(newAnnouncement.datetimeToSend) <= new Date()) {
      errors.datetimeToSend = "Please select a future date and time";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Table columns configuration for ResponsiveTable
  const tableColumns = [
    {
      key: "title",
      header: "Title",
      render: item => (
        <div className="flex items-center gap-2">
          <div className="max-w-[20ch] truncate" title={item.title}>
            {item.title}
          </div>
          {item.priority && item.priority !== "LOW" && (
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                item.priority === "HIGH"
                  ? "bg-red-100 text-red-700"
                  : item.priority === "URGENT"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-yellow-100 text-yellow-700"
              }`}
            >
              {item.priority}
            </span>
          )}
          {item.cta && (
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Action Required</span>
          )}
        </div>
      ),
      mobileLabel: "Title",
      mobileRender: item => (
        <div className="flex flex-col gap-1">
          <div>{item.title}</div>
          <div className="flex gap-1">
            {item.priority && item.priority !== "LOW" && (
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  item.priority === "HIGH"
                    ? "bg-red-100 text-red-700"
                    : item.priority === "URGENT"
                      ? "bg-orange-100 text-orange-700"
                      : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {item.priority}
              </span>
            )}
            {item.cta && (
              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">Action Required</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "body",
      header: "Message",
      render: item => (
        <div className="max-w-[30ch] truncate" title={item.body || "No message"}>
          {item.body || "No message"}
        </div>
      ),
      mobileLabel: "Message",
      mobileRender: item => item.body || "No message",
    },
    {
      key: "readStatus",
      header: "Status",
      render: item => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            item.readStatus ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
          }`}
        >
          {item.readStatus ? "Read" : "Unread"}
        </span>
      ),
      mobileLabel: "Status",
      mobileRender: item => (
        <span
          className={`px-2 py-1 text-xs rounded-full ${
            item.readStatus ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
          }`}
        >
          {item.readStatus ? "Read" : "Unread"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "Created At",
      render: item => formatCreatedAt(item.createdAt),
      mobileLabel: "Created At",
    },
    {
      key: "datetimeToSend",
      header: "Time to Publish",
      render: item => (
        <span
          className={isTimeInFuture(item.datetimeToSend) ? "text-green-600 font-medium" : "text-red-600 font-medium"}
        >
          {formatDateTime(item.datetimeToSend)}
        </span>
      ),
      mobileLabel: "Time to Publish",
      mobileRender: item => (
        <span
          className={isTimeInFuture(item.datetimeToSend) ? "text-green-600 font-medium" : "text-red-600 font-medium"}
        >
          {formatDateTime(item.datetimeToSend)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: item => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleEdit(item);
          }}
          className="text-blue-600 hover:text-blue-800 focus:outline-none"
        >
          <FaEllipsisH />
        </button>
      ),
      mobileLabel: "Actions",
      mobileRender: item => (
        <button
          onClick={e => {
            e.stopPropagation();
            handleEdit(item);
          }}
          className="text-blue-600 hover:text-blue-800 focus:outline-none"
        >
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="min-h-screen">
      <div className="p-4 md:p-6">
        {/* Page Header with Add Button */}
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-2xl font-bold">Announcements</h4>
          {canCreate && (
            <IconWordResponsiveButton icon={<FaPlus />} text="New Announcement" onClick={handleOpenCreateModal} />
          )}
        </div>

        {/* Announcements Table */}
        <div className="w-full mx-auto">
          <ResponsiveTable
            title="Recent Announcements"
            data={announcements}
            columns={canCreate ? tableColumns : tableColumns.filter(col => col.key !== "actions")}
            loading={isLoading}
            emptyMessage="No announcements found"
            onRowClick={canCreate ? announcement => handleEdit(announcement) : undefined}
          />

          {/* Pagination */}
          {announcements && announcements.length > 0 && (
            <div className="flex justify-center mt-6">
              <Pagination
                onPageChange={handlePageChange}
                totalCount={totalPages * pageSize}
                currentPage={currentPage + 1}
                pageSize={pageSize}
                className="pagination-bar"
              />
            </div>
          )}
        </div>
      </div>

      {/* Create New Announcement Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Announcement</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                name="title"
                value={newAnnouncement.title}
                onChange={handleInputChange}
                placeholder="Enter announcement title"
                required
                className={formErrors.title ? "border-red-500" : ""}
              />
              {formErrors.title && <p className="text-sm text-red-500">{formErrors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Message *</Label>
              <Textarea
                id="message"
                name="message"
                value={newAnnouncement.message}
                onChange={handleInputChange}
                placeholder="Enter announcement message"
                rows={3}
                required
                className={formErrors.message ? "border-red-500" : ""}
              />
              {formErrors.message && <p className="text-sm text-red-500">{formErrors.message}</p>}
            </div>

            {/* Select Roles */}
            <div className="space-y-2">
              <Label>Select Roles</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-between font-normal">
                    {newAnnouncement.selectedRoles.length > 0
                      ? `${newAnnouncement.selectedRoles.length} roles selected`
                      : "Select roles"}
                    <ChevronDownIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search roles..." />
                    <CommandList>
                      {availableRoles.map(role => (
                        <CommandItem
                          key={role.value}
                          onSelect={() => {
                            const isSelected = newAnnouncement.selectedRoles.includes(role.value);
                            setNewAnnouncement(prev => ({
                              ...prev,
                              selectedRoles: isSelected
                                ? prev.selectedRoles.filter(r => r !== role.value)
                                : [...prev.selectedRoles, role.value],
                            }));
                          }}
                        >
                          <Checkbox checked={newAnnouncement.selectedRoles.includes(role.value)} />
                          <span className="ml-2">{role.label}</span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <div className="flex flex-wrap gap-1">
                {newAnnouncement.selectedRoles.map(value => (
                  <Badge key={value} variant="secondary" className="flex items-center gap-1">
                    {getDisplayLabel(value)}
                    <button
                      onClick={() => handleDeleteRole(value)}
                      className="ml-1 hover:bg-gray-200 rounded-full w-4 h-4 flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-gray-500">Select one or more roles for the announcement</p>
            </div>

            {/* Date and Time Selection */}
            <div className="flex gap-4">
              <div className="flex flex-col gap-3 flex-1">
                <Label htmlFor="date">Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" id="date" className="w-full justify-between font-normal">
                      {selectedDate ? selectedDate.toLocaleDateString() : "Select date"}
                      <ChevronDownIcon className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={date => moment(date).isBefore(moment(), "day")}
                      initialFocus
                      classNames={{
                        caption: "flex justify-center pt-1 relative items-center",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex flex-col gap-3 flex-1">
                <Label htmlFor="time">Time *</Label>
                <Input
                  type="time"
                  id="time"
                  step="1"
                  value={selectedTime}
                  onChange={e => setSelectedTime(e.target.value)}
                  className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                />
              </div>
            </div>

            {/* Priority */}
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={newAnnouncement.priority}
                onValueChange={value => setNewAnnouncement(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="MEDIUM">Medium</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* CTA Settings */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasCTA"
                  checked={newAnnouncement.hasCTA}
                  onCheckedChange={checked => setNewAnnouncement(prev => ({ ...prev, hasCTA: checked }))}
                />
                <Label htmlFor="hasCTA">Include Call-to-Action</Label>
              </div>

              {newAnnouncement.hasCTA && (
                <div className="space-y-2">
                  <Label htmlFor="ctaValue">CTA Value</Label>
                  <Input
                    id="ctaValue"
                    name="ctaValue"
                    value={newAnnouncement.ctaValue}
                    onChange={handleInputChange}
                    placeholder="e.g., View Details, Book Now, etc."
                  />
                </div>
              )}
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label htmlFor="imageUpload">Images (Optional)</Label>
              <div className="space-y-3">
                {/* File Upload Input */}
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="imageUpload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg
                        className="w-8 h-8 mb-4 text-gray-500"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 20 16"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                        />
                      </svg>
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> images
                      </p>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 10MB each)</p>
                    </div>
                    <input
                      id="imageUpload"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={e => handleFileUpload(e.target.files)}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                )}

                {/* Uploaded Images Preview */}
                {newAnnouncement.imageUrls.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {newAnnouncement.imageUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`Uploaded ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">Upload images to include in the notification</p>
            </div>
          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseCreateModal}>
              Cancel
            </Button>
            <Button type="submit" onClick={handleSubmit} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Create Announcement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditAnnouncementModal
        isOpen={showEditModal}
        isClose={() => setShowEditModal(false)}
        onSubmit={handleUpdateAnnouncement}
        announcement={editingAnnouncement}
      />

      <AnnouncementFormModal
        isOpen={showConfirmModal}
        isClose={() => setShowConfirmModal(false)}
        onSubmit={handleConfirmSubmit}
        title="Confirm Announcement"
        declineButton="Cancel"
        acceptButton="Send"
        initialTitle={announcementToConfirm?.title}
        initialMessage={announcementToConfirm?.message}
        initialSelectedRoles={announcementToConfirm?.selectedRoles}
        initialUserIds={announcementToConfirm?.userIds}
        initialDatetimeToSend={announcementToConfirm?.datetimeToSend}
        readOnly={true}
        showRoles={announcementToConfirm?.selectedRoles?.length > 0}
        showUserIds={announcementToConfirm?.userIds?.length > 0}
        availableRoles={availableRoles}
      />
    </div>
  );
};

export default Announcements;
